import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";

import { Avatar, Menu, MenuItem, useToast } from "@ui/react";
import { useAuth } from "../lib/auth";
import { auth } from "../lib/firebase";

/**
 * Identity, account upkeep and the way out, at every width.
 *
 * Sign out used to have two entry points with two behaviours: the desktop
 * sidebar signed you out instantly, the Profile page asked "Sign out?" first —
 * and the sidebar disappeared below 600px, which is why the second one existed
 * at all. One control now, in the shell, on every route: immediate, with a
 * toast to say it happened. A confirmation dialog for an action that costs a
 * re-login is friction pretending to be safety.
 */
export function AccountMenu() {
  const { contractor, user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const toast = useToast();

  // A menu that survives the navigation it just caused would sit open over the
  // page it sent you to.
  const dismiss = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    return () => triggerRef.current?.focus();
  }, [open]);

  const name = `${contractor?.firstName ?? ""} ${contractor?.lastName ?? ""}`.trim();
  const email = contractor?.email ?? "";

  /* The only way to change a password or an email from inside the workspace:
     /forgot-password is gated to signed-OUT visitors, so a signed-in contractor
     is bounced off it to /jobs. Firebase client SDK, because contractor-api has
     no change-password endpoint — the same solution the homeowner site's
     Settings page uses. The address comes from the Firebase user rather than
     the profile document: it is the one Auth will actually send to. */
  const resetPassword = useCallback(async () => {
    const address = user?.email ?? email;
    if (!address) return;
    try {
      await sendPasswordResetEmail(auth(), address);
      toast.show(`Password reset link sent to ${address}.`);
    } catch {
      toast.show("We couldn't send that email. Please try again.");
    }
  }, [email, toast, user]);

  return (
    <div className="combobox">
      <button
        ref={triggerRef}
        type="button"
        className="avatar-btn"
        aria-label={name ? `Account: ${name}` : "Account"}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar
          name={name || email}
          size="sm"
          // The halo is the availability signal, not decoration — a contractor
          // who has switched themselves off should see it dark here too.
          available={contractor?.isAvailable}
        />
      </button>

      {open ? (
        <Menu align="end" onDismiss={dismiss}>
          <div className="menu__head">
            <span className="menu__name">{name || "Your account"}</span>
            {email ? <span className="menu__email">{email}</span> : null}
          </div>

          <MenuItem
            onClick={() => {
              dismiss();
              navigate("/profile");
            }}
          >
            Profile
          </MenuItem>

          <MenuItem
            onClick={() => {
              dismiss();
              void resetPassword();
            }}
          >
            Email me a password-reset link
          </MenuItem>

          <MenuItem
            onClick={() => {
              dismiss();
              void signOut().then(() => toast.show("You're signed out."));
            }}
          >
            Sign out
          </MenuItem>
        </Menu>
      ) : null}
    </div>
  );
}
